import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../../auth/hooks/useAuth';
import TtStatsDashboard from './TtStatsDashboard';
import CourseTableIndex from './CourseTableIndex';
import CourseDetailPage from './CourseDetailPage';
import TtScannerForm from './TtScannerForm';
import TtRecordsTable from './TtRecordsTable';
import TransferRecordsModal from './TransferRecordsModal';
import CourseCloseModal from './CourseCloseModal';
import SubmissionQueueTab from './SubmissionQueueTab';
import PendingRecordsTab from './PendingRecordsTab';
import RecordDetailModal from './RecordDetailModal';
import { useBatchQueue } from '../hooks/useBatchQueue';
import { getQueuePendingCountApi, savePendingRecordApi, transferExistingRecordApi } from '../services/trungTamApi';
import type { PendingRecordDto } from '../services/trungTamApi';
import {
  fetchCourses, createCourseApi, deleteCourseApi, updateCourseApi,
  fetchRecords, createRecordApi, deleteRecordApi, fetchStats, uploadDocumentApi, convertOneDvhcApi, updatePortraitApi,
  toggleReviewedApi, bulkTransferApi, bulkTransferPendingApi,
} from '../services/trungTamApi';
import type { RecordsPage } from '../services/trungTamApi';
import type { CloseCourseSubmit } from './CourseCloseModal';
import type {
  TtCourse, TtStudentRecord, TtStats, CccdScanResult, ScanMethod, TtCourseFormData, BatchItem, CourseStatus,
} from '../types';
import { CENTER_ACTIVE_HANG_VALUES } from '../types';
import '../trungTam.scss';

const TtHoSoPage: React.FC = () => {
  const { hasRole } = useAuth();
  const canWrite = hasRole('NhanVienNhapLieu');

  const [courses, setCourses] = useState<TtCourse[]>([]);
  // URL-backed state: ?khoa=N&tab=scanner|pending|queue|list
  // Null selectedCourseId → render level-1 table. Set → level-2 detail.
  // Reading URL on mount lets f5 / bookmark restore the exact view.
  const [selectedCourseId, setSelectedCourseIdState] = useState<number | null>(() => {
    if (typeof window === 'undefined') return null;
    const k = new URLSearchParams(window.location.search).get('khoa');
    const n = k ? parseInt(k, 10) : NaN;
    return Number.isFinite(n) ? n : null;
  });
  const [editingCourse, setEditingCourse] = useState<TtCourse | null>(null);
  const PAGE_SIZE = 50;
  const [recordsPage, setRecordsPage] = useState<RecordsPage>({ records: [], total: 0, page: 1, limit: PAGE_SIZE });
  const [stats, setStats] = useState<TtStats | null>(null);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [batchReviewItems, setBatchReviewItems] = useState<BatchItem[]>([]);
  const [selectedRecordIds, setSelectedRecordIds] = useState<Set<number>>(new Set());
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const [pendingCloseId, setPendingCloseId] = useState<number | null>(null);
  // Records of the course being closed. Populated by handleStatusChange
  // so the modal receives the right unreviewed set even when the user
  // closes a course from the Level-1 table (where `records` state is
  // empty because no course has been drilled into yet).
  const [pendingCloseRecords, setPendingCloseRecords] = useState<TtStudentRecord[]>([]);
  // Top-level tab: 'scanner' = direct-create; 'queue' = teacher submission queue;
  // 'pending' = new batch queue for silent-detect records waiting to save;
  // 'list' = final saved records list.
  const [activeView, setActiveViewState] = useState<'scanner' | 'list' | 'queue' | 'pending'>(() => {
    if (typeof window === 'undefined') return 'scanner';
    const t = new URLSearchParams(window.location.search).get('tab');
    if (t === 'pending' || t === 'queue' || t === 'list' || t === 'scanner') return t;
    return 'scanner';
  });

  // Keep URL in sync whenever selection / tab changes — lets f5 restore
  // the exact view, and browser back/forward walks the history stack.
  const setSelectedCourseId = useCallback((id: number | null) => {
    setSelectedCourseIdState(id);
    const url = new URL(window.location.href);
    if (id == null) {
      url.searchParams.delete('khoa');
      url.searchParams.delete('tab');
    } else {
      url.searchParams.set('khoa', String(id));
    }
    window.history.pushState({}, '', url.toString());
  }, []);

  const setActiveView = useCallback((t: 'scanner' | 'list' | 'queue' | 'pending') => {
    setActiveViewState(t);
    const url = new URL(window.location.href);
    url.searchParams.set('tab', t);
    window.history.replaceState({}, '', url.toString());
  }, []);

  // Listen for browser back/forward buttons so URL ↔ state stays synced.
  useEffect(() => {
    const onPop = () => {
      const params = new URLSearchParams(window.location.search);
      const k = params.get('khoa');
      const n = k ? parseInt(k, 10) : NaN;
      setSelectedCourseIdState(Number.isFinite(n) ? n : null);
      const t = params.get('tab');
      if (t === 'pending' || t === 'queue' || t === 'list' || t === 'scanner') {
        setActiveViewState(t);
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  const [queuePendingCount, setQueuePendingCount] = useState(0);

  // Batch queue — silent background detection for "cụm hồ sơ" entry.
  // State lives here (not in TtScannerForm) so detection continues running
  // while the operator navigates between tabs.
  const batchQueue = useBatchQueue();

  // Review modal — shown when operator clicks a record in "Chờ lưu hồ sơ"
  // OR clicks "Xem & lưu (N)". Wraps TtScannerForm in an overlay.
  const [reviewModalItems, setReviewModalItems] = useState<BatchItem[]>([]);
  const reviewModalOpen = reviewModalItems.length > 0;

  // Each file dropped into "Nhập theo lô CCCD" becomes its own QueueRecord
  // (N students). Different from addRecord which bundles multiple files
  // under a single QueueRecord (1 student with front/back/GPLX/...).
  const handleAddBatchFiles = useCallback((files: File[]) => {
    files.forEach(file => batchQueue.addRecord([file], selectedCourseId));
  }, [batchQueue, selectedCourseId]);

  // Poll queue pending count every 20s so the tab badge stays fresh
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const resp = await getQueuePendingCountApi();
        if (!cancelled && resp.EC === 0 && resp.DT) {
          setQueuePendingCount(resp.DT.count || 0);
        }
      } catch { /* ignore */ }
    };
    tick();
    const iv = setInterval(tick, 20_000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  // Load courses + stats on mount
  useEffect(() => {
    const load = async () => {
      try {
        const [courseRes, statsRes] = await Promise.all([fetchCourses(), fetchStats()]);
        if (courseRes.EC === 0) setCourses(courseRes.DT);
        if (statsRes.EC === 0) setStats(statsRes.DT);
      } catch {
        toast.error('Lỗi tải dữ liệu');
      }
    };
    load();
  }, []);

  // Load records when course changes — always resets to page 1.
  useEffect(() => {
    setSelectedRecordIds(new Set());
    if (!selectedCourseId) {
      setRecordsPage({ records: [], total: 0, page: 1, limit: PAGE_SIZE });
      return;
    }
    const load = async () => {
      setLoadingRecords(true);
      try {
        const res = await fetchRecords(selectedCourseId, 1, PAGE_SIZE);
        if (res.EC === 0) setRecordsPage(res.DT);
      } catch {
        toast.error('Lỗi tải hồ sơ');
      } finally {
        setLoadingRecords(false);
      }
    };
    load();
  }, [selectedCourseId]);

  const refreshAll = useCallback(async () => {
    try {
      const [courseRes, statsRes] = await Promise.all([fetchCourses(), fetchStats()]);
      if (courseRes.EC === 0) setCourses(courseRes.DT);
      if (statsRes.EC === 0) setStats(statsRes.DT);
    } catch { /* silent */ }
  }, []);

  const refreshRecords = useCallback(async (page?: number) => {
    if (!selectedCourseId) return;
    try {
      const targetPage = page ?? recordsPage.page;
      const res = await fetchRecords(selectedCourseId, targetPage, PAGE_SIZE);
      if (res.EC === 0) setRecordsPage(res.DT);
    } catch { /* silent */ }
  }, [selectedCourseId, recordsPage.page]);

  const handleToggleSelect = useCallback((id: number) => {
    setSelectedRecordIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleToggleReviewed = useCallback(async (id: number, reviewed: boolean) => {
    try {
      const resp = await toggleReviewedApi(id, reviewed);
      if (resp.EC === 0 && resp.DT) {
        setRecordsPage(prev => ({
          ...prev,
          records: prev.records.map(r => r.id === id ? { ...r, reviewed: resp.DT.reviewed } : r),
        }));
        if (reviewed) setSelectedRecordIds(prev => { const next = new Set(prev); next.delete(id); return next; });
      } else {
        toast.error(resp.EM || 'Lỗi cập nhật trạng thái duyệt');
      }
    } catch (err: unknown) {
      // Validation failures (missing CCCD/GPLX fields, unconverted DVHC) are
      // returned as 400 with a human-readable EM. Surface that directly.
      const e = err as { response?: { data?: { EM?: string } } };
      toast.error(e?.response?.data?.EM || 'Lỗi kết nối server');
    }
  }, []);

  const handleTransferred = useCallback(async () => {
    setSelectedRecordIds(new Set());
    await Promise.all([refreshRecords(), refreshAll()]);
  }, [refreshRecords, refreshAll]);

  const [pendingNewStatus, setPendingNewStatus] = useState<CourseStatus>('closed');

  const handleStatusChange = useCallback(async (courseId: number, newStatus: CourseStatus) => {
    // Closing or moving to pending_entry: check for residuals. We open
    // the split-transfer modal whenever the course has EITHER pending
    // rows OR unreviewed records — the modal's 2 sections each handle
    // their own category independently.
    if (newStatus === 'closed' || newStatus === 'pending_entry') {
      let courseRecords: TtStudentRecord[] = [];
      // Always refetch — closing from the Level-1 table means `records`
      // state is empty; closing the currently-selected course still
      // benefits from a fresh fetch so the modal reflects any edits made
      // right before the action. Fetch all (large limit) to get unreviewed count.
      try {
        const res = await fetchRecords(courseId, 1, 9999);
        courseRecords = res.EC === 0 ? res.DT.records : [];
      } catch { courseRecords = []; }
      const unreviewed = courseRecords.filter(r => !r.reviewed);
      const pendingForCourse = batchQueue.queueRecords.filter(p => p.ttCourseId === courseId);
      if (unreviewed.length > 0 || pendingForCourse.length > 0) {
        setPendingCloseRecords(courseRecords);
        setPendingCloseId(courseId);
        setPendingNewStatus(newStatus);
        setCloseModalOpen(true);
        return;
      }
    }

    // Simple status change with confirm
    const labels: Record<CourseStatus, string> = {
      open: 'Mở khóa',
      pending_entry: 'Chuyển sang chờ nhập CSĐT',
      closed: 'Đóng khóa',
    };
    if (!window.confirm(`${labels[newStatus]}?`)) return;

    try {
      const res = await updateCourseApi(courseId, { status: newStatus });
      if (res.EC === 0) {
        toast.success(`Đã ${labels[newStatus].toLowerCase()}`);
        await refreshAll();
        if (courseId === selectedCourseId) {
          setCourses(prev => prev.map(c => c.id === courseId ? { ...c, status: newStatus } : c));
        }
      } else {
        toast.error(res.EM);
      }
    } catch {
      toast.error('Lỗi cập nhật trạng thái');
    }
  }, [selectedCourseId, refreshAll, batchQueue.queueRecords]);

  const handleCloseModalConfirm = useCallback(async (submit: CloseCourseSubmit) => {
    if (!pendingCloseId) return;
    try {
      // Step 1 — transfer pending rows (chờ lưu hồ sơ) if requested.
      if (submit.pending.action !== 'keep' && submit.pending.targetCourseId && submit.pending.ids.length > 0) {
        const res = await bulkTransferPendingApi(submit.pending.ids, submit.pending.targetCourseId);
        if (res.EC !== 0) {
          toast.error(res.EM || 'Lỗi chuyển hồ sơ chờ lưu');
          return;
        }
        const dt = res.DT;
        if (dt) {
          toast.success(
            dt.skipped.length > 0
              ? `Đã chuyển ${dt.transferred} chờ lưu → "${dt.targetCourseName}" (bỏ qua ${dt.skipped.length})`
              : `Đã chuyển ${dt.transferred} chờ lưu → "${dt.targetCourseName}"`
          );
          dt.skipped.forEach(sk => toast.warn(`Bỏ qua ${sk.soCCCD || `#${sk.id}`}: ${sk.reason}`));
        }
      }

      // Step 2 — transfer unreviewed student records.
      if (submit.unreviewed.action !== 'keep' && submit.unreviewed.targetCourseId && submit.unreviewed.ids.length > 0) {
        const res = await bulkTransferApi(submit.unreviewed.ids, submit.unreviewed.targetCourseId);
        if (res.EC !== 0) {
          toast.error(res.EM || 'Lỗi chuyển hồ sơ chưa duyệt');
          return;
        }
        toast.success(`Đã chuyển ${res.DT?.transferred ?? submit.unreviewed.ids.length} chưa duyệt`);
      }

      // Step 3 — finally flip the course status.
      const statusLabel = submit.targetStatus === 'closed' ? 'Đã đóng khóa' : 'Đã chuyển sang chờ nhập CSĐT';
      const res = await updateCourseApi(pendingCloseId, { status: submit.targetStatus });
      if (res.EC === 0) {
        toast.success(statusLabel);
      } else {
        toast.error(res.EM);
      }
    } catch {
      toast.error('Lỗi cập nhật trạng thái');
    } finally {
      setCloseModalOpen(false);
      setPendingCloseId(null);
      setPendingCloseRecords([]);
      await Promise.all([refreshAll(), refreshRecords(), batchQueue.refreshQueue()]);
    }
  }, [pendingCloseId, refreshAll, refreshRecords, batchQueue]);

  /** Build the record payload from scan data. */
  const buildRecordPayload = (data: CccdScanResult & { scanMethod: ScanMethod }) => ({
    ttCourseId: selectedCourseId!,
    soCCCD: data.soCCCD,
    soCMT: data.soCMT,
    hoTen: data.hoTen,
    ngaySinh: data.ngaySinh,
    gioiTinh: data.gioiTinh,
    diaChi: data.diaChi,
    ngayCap: data.ngayCap,
    avatarUrl: data.avatarBase64 || undefined,
    portraitBase64: data.portraitBase64 || undefined,
    portraitOriginalBase64: data.portraitOriginalBase64 || undefined,
    portraitAiVariantsJson: data.portraitAiVariantsJson || undefined,
    faceMatchScore: data.faceMatchScore ?? undefined,
    sourceImageBase64: data.sourceImageBase64 || undefined,
    scanMethod: data.scanMethod,
    soGPLX: data.soGPLX || undefined,
    hangGPLX: data.hangGPLX || undefined,
    hangGPLXMoi: data.hangGPLXMoi || undefined,
    ngayCapGPLX: data.ngayCapGPLX || undefined,
    noiCapGPLX: data.noiCapGPLX || undefined,
    donViCapGPLX: data.donViCapGPLX || undefined,
    ngayTrungTuyenGPLX: data.ngayTrungTuyenGPLX || undefined,
    ngayHetHanGPLX: data.ngayHetHanGPLX || undefined,
    maDvhc: data.maDvhc || undefined,
    dvhcTenDayDu: data.dvhcTenDayDu || undefined,
    // Direct-entry flow: also persist extras as a single JSON blob so
    // the record detail popup can show them as "Ảnh bổ sung" — matches
    // the pending-save path for UI consistency.
    extraImagesJson: Array.isArray(data.extraImages) && data.extraImages.length > 0
      ? JSON.stringify(data.extraImages)
      : undefined,
  });

  const uploadExtras = async (recordId: number, data: CccdScanResult) => {
    if (!data.extraImages?.length) return;
    await Promise.all(
      data.extraImages.map(async (img) => {
        const blob = await fetch(img.base64).then(r => r.blob());
        const file = new File([blob], `doc-${Date.now()}.jpg`, { type: 'image/jpeg' });
        return uploadDocumentApi(recordId, file, img.docType, img.label).catch(() => null);
      })
    );
  };

  const handleAddRecord = async (data: CccdScanResult & { scanMethod: ScanMethod; pendingRecordId?: number }): Promise<boolean> => {
    if (!selectedCourseId) {
      toast.warn('Chọn khóa học trước khi thêm');
      return false;
    }
    const course = courses.find(c => c.id === selectedCourseId);
    if (course?.status !== 'open') {
      toast.error('Khóa học đã đóng, không thể thêm hồ sơ');
      return false;
    }

    // Branch: record originated from the "Chờ lưu hồ sơ" queue → use the
    // savePendingRecord API which atomically commits to tt_student_record
    // AND deletes the pending row. Handles the cross-course duplicate
    // guard that the old createRecordApi did not check.
    if (data.pendingRecordId) {
      const overrides = {
        soCCCD: data.soCCCD,
        soCMT: data.soCMT,
        hoTen: data.hoTen,
        ngaySinh: data.ngaySinh,
        gioiTinh: data.gioiTinh,
        diaChi: data.diaChi,
        ngayCap: data.ngayCap,
        soGPLX: data.soGPLX,
        hangGPLX: data.hangGPLX,
        hangGPLXMoi: data.hangGPLXMoi,
        ngayCapGPLX: data.ngayCapGPLX,
        ngayTrungTuyenGPLX: data.ngayTrungTuyenGPLX,
        ngayHetHanGPLX: data.ngayHetHanGPLX,
        maDvhc: data.maDvhc,
        dvhcTenDayDu: data.dvhcTenDayDu,
        // Carry the review modal's current extra images into the save
        // so edits (add/delete of CCCD back / GPLX back / health cert)
        // persist to tt_student_record.extraImagesJson. Without this, the
        // backend falls back to the pending row's stale copy and any
        // review-time changes are lost.
        extraImagesJson: Array.isArray(data.extraImages) && data.extraImages.length > 0
          ? JSON.stringify(data.extraImages)
          : null,
        // Carry full portrait state so pending → student record keeps
        // avatar + ảnh gốc + toàn bộ AI variants đã gen.
        portraitBase64: data.portraitBase64 || null,
        portraitOriginalBase64: data.portraitOriginalBase64 || null,
        portraitAiVariantsJson: data.portraitAiVariantsJson || null,
      };
      // Strict one-CCCD-one-record: duplicate → offer TRANSFER (move
      // existing record to new course) instead of force-save creating a
      // second row. No `force=true` bypass anymore.
      const promptTransfer = (dup: { courseName?: string; courseHang?: string; hoTen?: string; reviewed?: boolean }) => {
        const reviewedWarn = dup.reviewed
          ? '\n\n⚠ Hồ sơ ở khóa cũ ĐÃ DUYỆT. Chuyển sang khóa mới sẽ đặt lại trạng thái CHƯA DUYỆT.'
          : '';
        return window.confirm(
          `CCCD ${data.soCCCD} đã có trong hệ thống:\n\n` +
          `→ Khóa cũ: ${dup.courseName || '---'}\n` +
          `→ Hạng: ${dup.courseHang || '---'}\n` +
          `→ Học viên: ${dup.hoTen || '---'}${reviewedWarn}\n\n` +
          `Bạn có muốn CHUYỂN hồ sơ này sang khóa hiện tại không?\n` +
          `(Hệ thống chỉ giữ 1 bản ghi / CCCD — sẽ cập nhật thông tin mới từ hồ sơ đang nhập.)`,
        );
      };
      const doTransfer = async (): Promise<boolean> => {
        try {
          const res = await transferExistingRecordApi(data.pendingRecordId!, {
            courseId: selectedCourseId,
            overrides,
          });
          if (res.EC === 0 && res.DT) {
            toast.success(`Đã chuyển ${data.hoTen} sang khóa mới`);
            batchQueue.deleteLocal(data.pendingRecordId!);
            await Promise.all([refreshRecords(), refreshAll()]);
            return true;
          }
          toast.error(res.EM || 'Không chuyển được hồ sơ');
          return false;
        } catch (err: unknown) {
          const body = (err as { response?: { data?: { EM?: string } } })?.response?.data;
          toast.error(body?.EM || 'Lỗi chuyển hồ sơ');
          return false;
        }
      };
      try {
        const res = await savePendingRecordApi(data.pendingRecordId!, {
          courseId: selectedCourseId,
          overrides,
        });
        if (res.EC === 0) {
          toast.success(`Đã lưu ${data.hoTen}`);
          batchQueue.deleteLocal(data.pendingRecordId!);
          // Refresh in background so the modal closes IMMEDIATELY — operator
          // moves on to the next record without waiting for two list queries
          // (each up to 500 rows) to round-trip. The local optimistic state
          // already removed this record, so the UI is correct in the meantime.
          Promise.all([refreshRecords(), refreshAll()]).catch(() => {});
          return true;
        }
        if (res.EC === 2) {
          const dup = res.DT as unknown as { courseName?: string; courseHang?: string; hoTen?: string; reviewed?: boolean };
          if (promptTransfer(dup)) return doTransfer();
          return false;
        }
        // BE validation should never fire when FE validation is exhaustive,
        // but if it does (eg an operator hand-crafts a stale request),
        // surface the message ONCE — toastId dedupes against rapid clicks
        // and against any FE toast still on screen.
        toast.warn(res.EM || 'Không thể lưu hồ sơ', { toastId: 'pending-save-be-warn', autoClose: 4000 });
        return false;
      } catch (err: unknown) {
        const axiosErr = err as { response?: { status?: number; data?: { EC?: number; EM?: string; DT?: { courseName?: string; courseHang?: string; hoTen?: string; reviewed?: boolean } } } };
        const body = axiosErr?.response?.data;
        if (body?.EC === 2 || axiosErr?.response?.status === 409) {
          const dup = body?.DT || {};
          if (promptTransfer(dup)) return doTransfer();
          return false;
        }
        toast.error(body?.EM || 'Lỗi lưu hồ sơ');
        return false;
      }
    }

    try {
      const res = await createRecordApi(buildRecordPayload(data));
      if (res.EC === 0) {
        if (res.DT?.id) await uploadExtras(res.DT.id, data);
        toast.success(`Đã thêm ${data.hoTen}`);
        if (res.DT?.id) {
          convertOneDvhcApi(res.DT.id)
            .then(() => { void refreshRecords(); })
            .catch(() => { /* silent */ });
        }
        await Promise.all([refreshRecords(), refreshAll()]);
        return true;
      }
      toast.warn(res.EM || 'Không thể thêm hồ sơ');
      return false;
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: { EM?: string } } };
      if (axiosErr?.response?.status === 409) {
        toast.warn(`Hồ sơ trùng: ${data.hoTen} (${data.soCCCD}) đã tồn tại trong khóa học. Nhấn "Bỏ qua" để chuyển tiếp.`);
      } else {
        toast.error('Lỗi thêm hồ sơ');
      }
      return false;
    }
  };

  const handleDeleteRecord = async (id: number) => {
    try {
      const res = await deleteRecordApi(id);
      if (res.EC === 0) {
        toast.success('Đã xóa hồ sơ');
        await Promise.all([refreshRecords(), refreshAll()]);
      } else {
        toast.error(res.EM);
      }
    } catch {
      toast.error('Lỗi xóa hồ sơ');
    }
  };

  const handleCreateCourse = async (data: TtCourseFormData) => {
    if (!data.name.trim()) {
      toast.warning('Nhập tên khóa');
      return;
    }
    try {
      const res = await createCourseApi(data);
      if (res.EC === 0) {
        toast.success('Tạo khóa thành công');
        await refreshAll();
      } else {
        toast.error(res.EM);
      }
    } catch {
      toast.error('Tạo khóa thất bại');
    }
  };

  const handleDeleteCourse = async (id: number) => {
    // Confirmation now lives in CourseDetailPage's name-typing modal —
    // by the time we reach this handler the operator has already typed
    // the matching course name. No window.confirm needed.
    try {
      const res = await deleteCourseApi(id);
      if (res.EC === 0) {
        toast.success('Đã xóa khóa học');
        if (selectedCourseId === id) {
          setSelectedCourseId(null);
          setRecordsPage({ records: [], total: 0, page: 1, limit: PAGE_SIZE });
        }
        await refreshAll();
      } else {
        toast.error(res.EM);
      }
    } catch {
      toast.error('Lỗi xóa');
    }
  };

  const selectedCourse = courses.find(c => c.id === selectedCourseId);

  // Scope queue counts strictly to the currently-selected course. We
  // intentionally exclude legacy rows with ttCourseId=null — including
  // them inflated the badge/counter on every course (a row with no
  // course assignment showed up under all of them, so each course
  // appeared to have N pending when really only the orphans existed).
  const scopedQueueRecords = React.useMemo(() => {
    if (!selectedCourseId) return [] as PendingRecordDto[];
    return batchQueue.queueRecords.filter((r) => r.ttCourseId === selectedCourseId);
  }, [batchQueue.queueRecords, selectedCourseId]);

  const scopedPendingCount = scopedQueueRecords.filter(
    (r) => r.status === 'pending' || r.status === 'detecting',
  ).length;
  const scopedDoneCount = scopedQueueRecords.filter((r) => r.status === 'done').length;

  return (
    <div className="tt-page">
      {/* Page header */}
      <div className="tt-page__header">
        <h1 className="tt-page__title">
          <i className="material-icons">folder_shared</i>
          Nhập hồ sơ học viên
        </h1>
        {!canWrite && (
          <span className="tt-page__readonly-badge">
            <i className="material-icons">visibility</i> Chỉ xem
          </span>
        )}
      </div>

      {/* Stats */}
      <TtStatsDashboard stats={stats} />

      {/* Level 1: course table. Shown when no course is selected. */}
      {!selectedCourseId && (
        <CourseTableIndex
          courses={courses}
          canWrite={canWrite}
          onSelect={setSelectedCourseId}
          onCreate={handleCreateCourse}
          onEdit={setEditingCourse}
        />
      )}

      {/* Level 2: detail page for the selected course. Wrapper renders
          breadcrumb + summary + tab bar; children are the active tab's
          content (scanner / pending / queue / list). */}
      {selectedCourseId && selectedCourse && (
        <CourseDetailPage
          course={selectedCourse}
          activeTab={activeView}
          onChangeTab={setActiveView}
          onBack={() => setSelectedCourseId(null)}
          onEditCourse={setEditingCourse}
          onStatusChange={handleStatusChange}
          onDeleteCourse={handleDeleteCourse}
          canWrite={canWrite}
          counts={{
            pending: scopedQueueRecords.length,
            queue: queuePendingCount,
            list: recordsPage.total,
          }}
        >
          {/* Tab: Nhập hồ sơ trực tiếp */}
          {activeView === 'scanner' && canWrite && selectedCourse.status === 'open' && (
            <TtScannerForm
              simpleMode
              onSubmit={handleAddRecord}
              onAddToQueue={(files) => batchQueue.addRecord(files, selectedCourseId)}
              onAddToQueueWithHints={(items) => batchQueue.addRecord(items, selectedCourseId)}
              onAddBatchFiles={handleAddBatchFiles}
              queueCount={scopedQueueRecords.length}
              queuePendingCount={scopedPendingCount}
              queueDoneCount={scopedDoneCount}
              isNangHangCourse={!!selectedCourse.isNangHang}
            />
          )}
          {activeView === 'scanner' && selectedCourse.status !== 'open' && (
            <div className="tt-course-detail__closed-banner">
              <i className="material-icons">lock</i>
              Khóa đã {selectedCourse.status === 'closed' ? 'đóng' : 'chờ nhập'}, không thể nhập hồ sơ mới.
            </div>
          )}

          {/* Tab: Chờ lưu hồ sơ — uses scopedQueueRecords (pending rows
              for this course + legacy ttCourseId=null rows). Unscoped
              ones get tagged when the operator saves them into the
              current course. */}
          {activeView === 'pending' && (
            <PendingRecordsTab
              records={scopedQueueRecords}
              isProcessing={batchQueue.queueProcessing}
              doneCount={scopedDoneCount}
              errorCount={scopedQueueRecords.filter(r => r.status === 'error').length}
              pendingCount={scopedPendingCount}
              onReview={(items) => setReviewModalItems(items)}
              onClear={batchQueue.clearQueue}
              onRemove={batchQueue.removeRecord}
              onRetry={batchQueue.retryRecord}
              courses={courses}
              currentCourseId={selectedCourseId}
              currentCourseHang={selectedCourse?.hang || ''}
              onTransferred={async () => {
                await Promise.all([batchQueue.refreshQueue(), refreshAll()]);
              }}
            />
          )}

          {/* Tab: Hàng đợi từ giáo viên */}
          {activeView === 'queue' && (
            <SubmissionQueueTab
              courses={courses}
              selectedCourseId={selectedCourseId}
              onRecordCreated={() => { refreshRecords(); refreshAll(); }}
            />
          )}

          {/* Tab: Danh sách hồ sơ */}
          {activeView === 'list' && (
            <div className="tt-records">
              <TtRecordsTable
                records={recordsPage.records}
                loading={loadingRecords}
                canWrite={canWrite}
                courseStatus={selectedCourse.status}
                courseId={selectedCourseId}
                selectedIds={selectedRecordIds}
                onToggleSelect={handleToggleSelect}
                onToggleReviewed={handleToggleReviewed}
                onOpenTransferModal={() => setTransferModalOpen(true)}
                onOpenSingleTransfer={(id) => {
                  setSelectedRecordIds(new Set([id]));
                  setTransferModalOpen(true);
                }}
                onDelete={handleDeleteRecord}
                onRecordUpdated={(updated) => {
                  setRecordsPage((prev) => ({
                    ...prev,
                    records: prev.records.map((r) => r.id === updated.id ? { ...r, ...updated } : r),
                  }));
                }}
                onRequestRecordsReload={refreshRecords}
                page={recordsPage.page}
                total={recordsPage.total}
                pageSize={PAGE_SIZE}
                onPageChange={(p) => refreshRecords(p)}
              />
            </div>
          )}
        </CourseDetailPage>
      )}

      {selectedCourseId && (
        <TransferRecordsModal
          open={transferModalOpen}
          onClose={() => setTransferModalOpen(false)}
          onTransferred={handleTransferred}
          selectedIds={selectedRecordIds}
          courses={courses}
          currentCourseId={selectedCourseId}
        />
      )}

      {pendingCloseId && (
        <CourseCloseModal
          open={closeModalOpen}
          onClose={() => { setCloseModalOpen(false); setPendingCloseId(null); setPendingCloseRecords([]); }}
          onConfirm={handleCloseModalConfirm}
          pendingRecords={batchQueue.queueRecords.filter(p => p.ttCourseId === pendingCloseId)}
          unreviewedRecords={pendingCloseRecords.filter(r => !r.reviewed)}
          openCourses={courses.filter(c => c.status === 'open')}
          currentCourseId={pendingCloseId}
          currentCourseHang={courses.find(c => c.id === pendingCloseId)?.hang || ''}
          targetStatus={pendingNewStatus}
        />
      )}

      {/* Review & save modal — opens from "Chờ lưu hồ sơ" row click or
           "Xem & lưu (N)" button. Wraps TtScannerForm in batch review. */}
      <RecordDetailModal
        open={reviewModalOpen}
        items={reviewModalItems}
        courses={courses}
        selectedCourseId={selectedCourseId}
        onClose={() => setReviewModalItems([])}
        onSubmit={async (data) => {
          const ok = await handleAddRecord(data);
          if (ok) {
            // Drop the just-saved item from modal state. When empty, the
            // modal auto-closes (RecordDetailModal guards against it).
            setReviewModalItems(prev => {
              // Find and remove by pendingRecordId — the only stable key
              // when multiple items share a name/CCCD shape.
              const idx = prev.findIndex(p => p.pendingRecordId === data.pendingRecordId);
              if (idx < 0) return prev;
              return [...prev.slice(0, idx), ...prev.slice(idx + 1)];
            });
          }
          return ok;
        }}
      />

      {/* Edit course modal — opened from both the index table and the
          detail header. Simple inline form; uses updateCourseApi and
          refreshes the list on success so counts propagate. */}
      {editingCourse && canWrite && (
        <EditCourseModal
          course={editingCourse}
          onClose={() => setEditingCourse(null)}
          onSaved={async () => {
            setEditingCourse(null);
            await refreshAll();
          }}
        />
      )}
    </div>
  );
};

// Uses the center's active-hang list — edit modal stays in sync with
// the create modal, so a rename/add of a hang at the center only needs
// to happen in one place (CENTER_ACTIVE_HANGS in types.ts).
const LICENSE_CLASSES_EDIT: string[] = CENTER_ACTIVE_HANG_VALUES;

const EditCourseModal: React.FC<{
  course: TtCourse;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}> = ({ course, onClose, onSaved }) => {
  const [name, setName] = useState(course.name);
  const [hang, setHang] = useState(course.hang);
  const [isNangHang, setIsNangHang] = useState(!!course.isNangHang);
  const [maxReviewed, setMaxReviewed] = useState<number | ''>(course.maxReviewed ?? '');
  const [saving, setSaving] = useState(false);
  const quotaInvalid = typeof maxReviewed === 'number' && maxReviewed > 0 && (course.reviewedCount ?? 0) > maxReviewed;

  return (
    <div className="tt-course-bar__modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="tt-course-bar__modal">
        <div className="tt-course-bar__modal-head">
          <i className="material-icons">edit</i>
          <h3>Sửa khóa &quot;{course.name}&quot;</h3>
          <button type="button" className="tt-course-bar__modal-close" onClick={onClose}>
            <i className="material-icons">close</i>
          </button>
        </div>
        <div className="tt-course-bar__modal-body">
          <label>
            Tên khóa học
            <input type="text" value={name} onChange={e => setName(e.target.value)} autoFocus />
          </label>
          <label>
            Hạng GPLX
            <select value={hang} onChange={e => setHang(e.target.value as typeof hang)}>
              {LICENSE_CLASSES_EDIT.map(h => <option key={h} value={h}>Hạng {h}</option>)}
            </select>
          </label>
          <label>
            Giới hạn số hồ sơ được duyệt
            <input
              type="number"
              min={0}
              placeholder="Để trống = không giới hạn"
              value={maxReviewed}
              onChange={e => {
                const v = e.target.value;
                setMaxReviewed(v === '' ? '' : Math.max(0, parseInt(v, 10) || 0));
              }}
            />
            <small style={{ color: '#888', fontWeight: 400, fontSize: '0.7rem' }}>
              Hiện tại đã duyệt: <strong>{course.reviewedCount ?? 0}</strong> hồ sơ.
            </small>
          </label>
          {quotaInvalid && (
            <div style={{
              padding: '8px 10px', background: '#fff4e5', border: '1px solid #ffcc80',
              borderRadius: 4, fontSize: '0.78rem', color: '#b26500',
            }}>
              ⚠ Giới hạn nhỏ hơn số đã duyệt. Giảm số duyệt trước rồi hãy đặt giới hạn này.
            </div>
          )}
          <label className="tt-course-bar__modal-check">
            <input type="checkbox" checked={isNangHang} onChange={e => setIsNangHang(e.target.checked)} />
            Khóa nâng hạng (yêu cầu đủ thông tin GPLX khi duyệt)
          </label>
        </div>
        <div className="tt-course-bar__modal-foot">
          <button type="button" className="tt-btn tt-btn--outline" onClick={onClose} disabled={saving}>Hủy</button>
          <button
            type="button"
            className="tt-btn tt-btn--primary"
            disabled={saving || !name.trim() || quotaInvalid}
            onClick={async () => {
              setSaving(true);
              try {
                const resp = await updateCourseApi(course.id, {
                  name: name.trim(),
                  hang,
                  isNangHang,
                  maxReviewed: maxReviewed === '' ? null : maxReviewed,
                });
                if (resp.EC === 0) {
                  toast.success('Cập nhật khóa thành công');
                  await onSaved();
                } else {
                  toast.error(resp.EM || 'Không cập nhật được khóa');
                }
              } catch (err: unknown) {
                const m = (err as { response?: { data?: { EM?: string } } })?.response?.data?.EM;
                toast.error(m || 'Lỗi cập nhật khóa');
              } finally {
                setSaving(false);
              }
            }}
          >
            {saving ? 'Đang lưu…' : 'Lưu'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TtHoSoPage;
